import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Award, BookOpen, ClipboardCheck, Users, ShoppingBag, Copy, Gift, Share2 } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5 text-gold-700" />
      <span className="font-mono text-xs text-muted2 uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-display text-3xl text-ink-900">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/user/dashboard").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-10 text-center text-muted2 font-mono text-sm">Chargement…</div>;

  const referralLink = `${window.location.origin}/register?ref=${user.referral_code}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="dashboard-page">
      <div className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-3">Tableau de bord</div>
      <h1 className="font-display text-4xl md:text-5xl text-ink-900">Bonjour, {user.full_name.split(" ")[0]} 👋</h1>
      <p className="text-muted2 mt-2">Voici votre progression et vos certificats.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
        <Stat icon={BookOpen} label="leçons" value={data.stats.lessons_completed} testid="stat-lessons" />
        <Stat icon={ClipboardCheck} label="quiz" value={data.stats.quizzes_passed} testid="stat-quizzes" />
        <Stat icon={Award} label="certificats" value={data.stats.certificates} testid="stat-certificates" />
        <Stat icon={ShoppingBag} label="achats" value={data.stats.purchases} testid="stat-purchases" />
        <Stat icon={Users} label="parrainages" value={data.stats.referrals} testid="stat-referrals" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="glass rounded-2xl p-6">
          <h2 className="font-display text-2xl text-ink-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-gold-700" /> Mes certificats
          </h2>
          {data.certificates.length === 0 ? (
            <p className="text-muted2 text-sm">Aucun certificat pour l'instant. <Link to="/categories" className="text-gold-700 hover:text-gold-700-700-600">Explorez les catégories →</Link></p>
          ) : (
            <ul className="space-y-2">
              {data.certificates.map(c => (
                <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg border border-ink-200">
                  <div className="min-w-0">
                    <div className="text-ink-900 text-sm truncate">{c.certification_title}</div>
                    <div className="text-xs text-muted2 font-mono">{c.unique_code}</div>
                  </div>
                  <Link to={`/certificates/${c.id}`} className="text-xs text-gold-700 hover:text-gold-700-700-600 whitespace-nowrap" data-testid={`view-cert-${c.unique_code}`}>Voir →</Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass rounded-2xl p-6">
          <h2 className="font-display text-2xl text-ink-900 mb-2 flex items-center gap-2">
            <Gift className="w-5 h-5 text-gold-700" /> Parrainer un ami
          </h2>
          <p className="text-sm text-muted2 mb-4">
            Pour chaque ami parrainé qui achète une certification, vous gagnez <strong className="text-gold-700">10€ de crédit</strong>.
          </p>
          <div className="font-mono text-xs px-3 py-2 bg-white border border-gold-200 rounded-lg flex items-center justify-between gap-2">
            <span className="truncate" data-testid="referral-link">{referralLink}</span>
            <button onClick={()=>{ navigator.clipboard.writeText(referralLink); toast.success("Lien copié !"); }} className="text-gold-700 hover:text-gold-700-700-600" data-testid="copy-referral-link">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-muted2">Votre crédit disponible</span>
            <span className="font-display text-2xl text-gold-700-gradient">{user.credit_euros?.toFixed(2) || "0.00"}€</span>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6 mt-6">
        <h2 className="font-display text-2xl text-ink-900 mb-4">Mes formations en cours</h2>
        {data.purchases.length === 0 ? (
          <p className="text-muted2 text-sm">Aucun achat pour l'instant.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.purchases.map(p => (
              <Link key={p.id} to={`/learn/${p.certification?.slug}`} className="border border-gold-200 rounded-lg p-4 hover:bg-gold-500/5 transition" data-testid={`purchase-${p.id}`}>
                <div className="text-xs uppercase tracking-widest text-gold-700/80 font-mono mb-1">{p.status === "succeeded" ? "Accès actif" : p.status}</div>
                <div className="text-ink-900 text-sm">{p.certification?.title || "Certification"}</div>
                <div className="text-xs text-muted2 mt-1">{new Date(p.purchased_at).toLocaleDateString("fr-FR")}</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        {user.profile_public && (
          <Link to={`/profil/${user.referral_code}`} target="_blank" className="text-sm text-gold-700 hover:text-gold-800 inline-flex items-center gap-1 font-medium" data-testid="link-public-profile">
            <Share2 className="w-4 h-4" /> Voir mon profil public
          </Link>
        )}
        <Link to="/account" className="text-sm text-ink-500 hover:text-ink-900" data-testid="link-account-settings">Paramètres et profil public →</Link>
      </div>
    </div>
  );
}
